/*
  CategoryIcon — refined monoline icons for the "big 4" catalogue categories.
  ───────────────────────────────────────────────────────────────────────────
  A lux replacement for the old category emoji glyphs. Each icon is drawn
  with `currentColor` strokes so callers can tint it — the homepage carousels
  paint them in the category's accent ink over a soft gradient, while the navbar
  dropdown lets them inherit the link colour so they adapt to light/dark.

  Keyed by the category `value` from constants/categories.js.
*/

const ICONS = {
  // Balm / lipstick stick — tube body with a rising domed bullet.
  sticks: (
    <>
      <rect x="8.6" y="11.4" width="6.8" height="9.6" rx="1.4" />
      <path d="M10 11.4V7.6a2 2 0 0 1 4 0v3.8" />
      <path d="M8.6 14.1h6.8" />
    </>
  ),
  // Dewdrop — a single droplet with an inner light-catch arc.
  skin: (
    <>
      <path d="M12 3.4c3.3 4.2 5.1 6.7 5.1 9.3a5.1 5.1 0 0 1-10.2 0c0-2.6 1.8-5.1 5.1-9.3z" />
      <path d="M9.6 13.4a2.6 2.6 0 0 0 2.4 2.6" />
    </>
  ),
  // Botanical leaf with a centre vein — for scalp & hair rituals.
  hair: (
    <>
      <path d="M5 19C5 11 11 5 19 5c0 8-6 14-14 14z" />
      <path d="M9.4 14.6 15 9" />
    </>
  ),
  // Peptide double-helix — two strands with connecting rungs.
  peptides: (
    <>
      <path d="M8 3c0 4 8 5 8 9s-8 5-8 9" />
      <path d="M16 3c0 4-8 5-8 9s8 5 8 9" />
      <path d="M9.2 7.4h5.6M9.2 16.6h5.6" />
    </>
  ),
};

// Generic glyph for admin-added categories that don't map to a bespoke icon —
// a faceted gem, on-brand with the beauty/skincare set.
const FALLBACK_GLYPH = (
  <>
    <path d="M7 4h10l3 5-8 11L4 9z" />
    <path d="M4 9h16M9 4l-2 5 5 11M15 4l2 5-5 11" />
  </>
);

export default function CategoryIcon({ category, className, ...props }) {
  const glyph = ICONS[category] || (category ? FALLBACK_GLYPH : null);
  if (!glyph) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      {glyph}
    </svg>
  );
}
